import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  coverSummary,
  fetchColleagues,
  fetchUncovered,
  type CoverRow,
} from '../api/cover'
import { raiseAsk } from '../api/asks'
import { Link } from 'react-router-dom'

/**
 * Everything still waiting on somebody (D-328, D-332).
 *
 * **The half of cover that only Tonya can do.** A teacher can say she is out
 * and name somebody; if she names nobody, or the person she named says no, the
 * row sits in `Uncovered` and there was no screen in the building that showed
 * it. `fetchUncovered` and `askToCover` shipped with the endpoints and had no
 * caller at all — the API could answer a question nothing asked.
 *
 * **Re-asking is the same move as asking**, which is why this needs no second
 * verb: a decline returns the row to `Uncovered` rather than to a resting
 * `Declined` state, so "nobody yet" and "the last person said no" are the same
 * queue with a different sentence. `coverSummary` writes both.
 *
 * **Gated on `ManageClasses`**, matching the endpoint exactly rather than
 * approximately — a screen reachable by somebody the API refuses is a link
 * that lies (D-324).
 */
export default function AdminCoverView() {
  const queryClient = useQueryClient()
  const [asking, setAsking] = useState<Record<string, string>>({})

  const { data, isPending, isError } = useQuery({
    queryKey: ['cover', 'uncovered'],
    queryFn: fetchUncovered,
  })

  /*
   * **Its own failure, said out loud.** Two requests join in this browser and
   * one succeeding while the other fails is the normal case after a deploy, not
   * the rare one (D-209). Without this the queue renders perfectly, the select
   * shows only its placeholder, and the Ask button is disabled forever with
   * nothing on screen explaining why.
   */
  const { data: colleagues, isError: noColleagues } = useQuery({
    queryKey: ['cover', 'colleagues'],
    // No day: this list is who may stand in at all, not who is free tonight.
    queryFn: () => fetchColleagues(),
  })

  const ask = useMutation({
    mutationFn: ({ id, staffId }: { id: string; staffId: string }) =>
      raiseAsk({ kind: 'cover', to: { type: 'Staff', id: staffId }, subject: { type: 'Cover', id } }),
    onSuccess: (_result, { id }) => {
      /*
       * **Forget who was chosen**, or the select still names her after she has
       * been asked — and when she declines, the row returns to `Uncovered`
       * (D-328) with her still selected and the button live. The sentence says
       * "the last person asked said no" while the control sits ready to ask her
       * again, which is the warning and the invitation in the same card.
       */
      setAsking((prev) => {
        const { [id]: _asked, ...rest } = prev
        return rest
      })

      /*
       * The night may have moved, so anything showing it is stale (D-330) —
       * the same set `CoverPanel` invalidates, for the same reason.
       */
      queryClient.invalidateQueries({ queryKey: ['cover'] })
      queryClient.invalidateQueries({ queryKey: ['teacher-home'] })
      queryClient.invalidateQueries({ queryKey: ['sheet'] })
      queryClient.invalidateQueries({ queryKey: ['registers'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const rows = data ?? []

  return (
    <section>
      {/*
        Three states, because "nothing" and "could not ask" are different facts
        and only one of them can be described (D-209).
      */}
      {isPending && <p className="text-[13.5px] text-ink-3">Loading&hellip;</p>}

      {isError && (
        <p role="alert" className="text-[13.5px] text-crit">
          Could not load the cover queue just now. Please try again.
        </p>
      )}

      {!isPending && !isError && rows.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-6 text-center text-[13.5px] text-ink-3">
          Every class has somebody. Anyone who says they are out will show up here.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <Row
            key={row.id}
            row={row}
            /*
             * **Per row, because who may cover depends on who is out.**
             * `/cover/colleagues` removes the *caller* — correct on a teacher's
             * own panel, where she is the one who is out, and wrong here, where
             * the caller is Tonya. Unfiltered, the absent teacher was offered as
             * her own coverer on every row, and picking her took a 409.
             */
            colleagues={(colleagues ?? []).filter((c) => c.id !== row.absentStaffId)}
            value={asking[row.id] ?? ''}
            onChange={(v) => setAsking((prev) => ({ ...prev, [row.id]: v }))}
            onAsk={() => ask.mutate({ id: row.id, staffId: asking[row.id] ?? '' })}
            /*
             * Only the row being asked, not all of them: one shared mutation
             * disabled every button in the list while any request was in flight.
             */
            busy={ask.isPending && ask.variables?.id === row.id}
          />
        ))}
      </div>

      {/*
        Only with rows to act on: with an empty queue there is nobody to ask,
        and "could not load the queue" above is already carrying that case.
      */}
      {noColleagues && rows.length > 0 && (
        <p className="mt-3 text-[13px] text-crit">
          Could not load who is available to ask. The list above is still right.
        </p>
      )}

      {/*
        **Nobody to ask reads exactly like a broken lookup**, and only one of
        them is worth apologising for. This fires when the list loaded and came
        back empty — every teaching colleague is the one already out, or the
        studio has one teacher — which the disabled button alone cannot explain.
      */}
      {!noColleagues && colleagues?.length === 0 && rows.length > 0 && (
        <p className="mt-3 text-[13px] text-ink-3">
          There is nobody else on staff to ask.
        </p>
      )}

      {/*
        The server writes the useful sentence — "That session has already
        started", "She is not on your staff" — and swallowing it leaves a person
        tapping a button that visibly does nothing.
      */}

      {ask.error && <p className="mt-3 text-[13px] text-crit">{(ask.error as Error).message}</p>}
    </section>
  )
}

function Row({
  row,
  colleagues,
  value,
  onChange,
  onAsk,
  busy,
}: {
  row: CoverRow
  colleagues: { id: string; name: string }[]
  value: string
  onChange: (v: string) => void
  onAsk: () => void
  busy: boolean
}) {
  const when = new Date(`${row.date}T${row.startTime ?? '00:00:00'}`)

  /*
   * **Not `Card` from `CoverPanel`, and that is the finding rather than the
   * shortcut.** Its one `mine` flag drives two things at once — whether to name
   * who is out, and whether to say "you" — and neither value is right for a
   * third party: `false` hides nothing but claims *she* is covering, `true`
   * names the coverer but drops the absent teacher entirely. One flag
   * answering two questions is exactly what D-328 already had to split once.
   */
  return (
    <article className="rounded-2xl border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        {/* Machine-shaped and unbreakable to CSS, so it wraps anywhere. */}
        <h3 className="wrap-anywhere font-semibold text-[14px] text-ink">{row.className}</h3>

        <p className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          {when.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
          {row.startTime && (
            <span> &middot; {when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
          )}
        </p>
      </div>

      <p className="mt-1 text-[13.5px] text-ink-2">
        <strong className="font-semibold">{row.absentName}</strong> is out.{' '}
        {/*
          `true` because Tonya is never the person asked. The flag chooses
          between naming somebody and saying "you" — an onlooker gets the name,
          which is the whole reason she is reading this.
        */}
        {coverSummary(row, true)}
      </p>

      {row.note && <p className="mt-1 text-[13px] text-ink-3">&ldquo;{row.note}&rdquo;</p>}

      {/*
        **Her decision, and it is the only thing offered on this row** (D-540).

        The ask control is hidden while somebody is waiting, because `Ask`
        refuses from this state on the server — leaving it would be a button
        that takes a 409, and it would also be the one path that discards a
        student teacher's yes without telling her.
      */}
      {row.state === 'AwaitingApproval' ? (
        <p className="mt-2 text-[13px] text-ink-2">
          {/*
            The studio's yes or no is an ask on the one list (D-540, D-552),
            answered there with every other question waiting on her; this
            screen is the subject's, and it says where the answer lives.
          */}
          <Link to="/asks" className="font-semibold text-accent hover:underline">
            Answer on Requests
          </Link>
        </p>
      ) : (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor={`ask-${row.id}`}>
          Ask somebody to cover {row.className}
        </label>

        <select
          id={`ask-${row.id}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] text-ink"
        >
          <option value="">Ask somebody&hellip;</option>
          {colleagues.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={onAsk}
          disabled={busy || !value}
          className="rounded-lg bg-accent px-3 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-60"
        >
          {row.state === 'Asked' ? 'Ask instead' : 'Ask'}
        </button>
      </div>
      )}
    </article>
  )
}
